// Finds the person's own words in a plain-text draft, apart from their signature and the email
// they answer or forward, so writing help rewrites only that part and leaves the rest alone.

/** The line that opens a forwarded email; everything from it on belongs to someone else. */
const FORWARD_HEADER = "---------- Forwarded message ----------";

/** A draft as lines: what comes before the person's part, their part, and what follows. */
export type PlainParts = { before: string[]; own: string[]; after: string[] };

const blank = (lines: readonly string[]) => lines.every((line) => !line.trim());

/**
 * Splits plain text around the person's part. It ends at their signature or at a forwarded
 * email, and sits above the quoted email, or below it when they answer there (`answerBelow`
 * picks below when nothing is written yet). Text typed between quoted lines stays where it is.
 */
export const splitPlain = (text: string, answerBelow = false): PlainParts => {
  const lines = text.split("\n");
  const ends = [
    lines.findIndex((line) => line.trimEnd() === "--"),
    lines.findIndex((line) => line.trim() === FORWARD_HEADER),
  ].filter((index) => index !== -1);
  const end = ends.length > 0 ? Math.min(...ends) : lines.length;
  const quoted = lines.slice(0, end).map((line) => line.startsWith(">"));
  const firstQuote = quoted.indexOf(true);
  const lastQuote = quoted.lastIndexOf(true);
  let start = 0;
  let stop = end;
  if (firstQuote !== -1) {
    // The quote's "On …, … wrote:" line.
    const intro = Math.max(0, firstQuote - 1);
    const below = lines.slice(lastQuote + 1, end);
    // Answering below the quote: written there already, or nothing above it and room below
    // (or the person's setting says so).
    if (!blank(below) || (blank(lines.slice(0, intro)) && (below.length > 0 || answerBelow)))
      start = lastQuote + 1;
    else stop = intro;
  }
  // Blank lines around their part stay where they are.
  while (start < stop && !lines[start]?.trim()) start++;
  while (stop > start && !lines[stop - 1]?.trim()) stop--;
  return { before: lines.slice(0, start), own: lines.slice(start, stop), after: lines.slice(stop) };
};

/** Puts new text in place of the person's part, with a blank line around it where there was none. */
export const joinPlain = (parts: PlainParts, text: string) => {
  const lines = text.split("\n");
  if (parts.own.length === 0) {
    if (parts.before[parts.before.length - 1]?.trim()) lines.unshift("");
    if (parts.after[0]?.trim()) lines.push("");
  }
  return [...parts.before, ...lines, ...parts.after].join("\n");
};
